import type { EmbedPlatform } from "./embedFilter.types";

export type { EmbedPlatform };

/**
 * Infers the BI platform from an embed URL when the backend did not name the
 * source. Covers Tableau Cloud and custom-domain Tableau Server (by the shape
 * of the path, not only the host) and Power BI.
 */
export function detectEmbedPlatform(
  embedUrl: string | undefined,
): EmbedPlatform | undefined {
  if (!embedUrl) return undefined;
  if (/powerbi\.com|powerbi\.microsoft\.com|\/(report|visual|dashboard)Embed/i.test(embedUrl)) {
    return "powerbi";
  }
  if (
    /\/views\/|\/#\/site\/|\/t\/[^/?#]+\/views\/|[?&#]:embed=|\/trusted\/|tableau/i.test(
      embedUrl,
    )
  ) {
    return "tableau";
  }
  return undefined;
}

/** Maps a stored connector source key (tableau, power_bi, ...) to an embed platform. */
export function toEmbedPlatform(
  source: string | undefined | null,
): EmbedPlatform | undefined {
  const key = source?.trim().toLowerCase().replace(/[\s_-]+/g, "");
  if (key === "tableau") return "tableau";
  if (key === "powerbi") return "powerbi";
  if (key === "salesforce") return "salesforce";
  return undefined;
}

/**
 * Extracts the `reportId` query param from a Power BI `embedUrl` — this is
 * the report GUID (`ConnectorAsset.external_id`) the backend scopes the
 * embed token to via GenerateToken. Power BI's SDK requires this same GUID
 * as the `id` in `powerbi.embed()`; passing anything else (e.g. our internal
 * connector_asset_id) causes the SDK's post-embed calls to 403.
 */
export function extractPowerBiReportId(
  embedUrl: string | undefined,
): string | undefined {
  if (!embedUrl) return undefined;
  try {
    return new URL(embedUrl).searchParams.get("reportId") ?? undefined;
  } catch {
    return undefined;
  }
}
